// ============================================================
// PICKUP QUEUE — Live queue of active orders requiring action
// Auto-refreshes every 30 seconds; shows elapsed timers
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, RefreshCw, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button, StatCard } from '../components/ui';
import { getPickupQueue } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../utils/constants';
import { toast } from 'react-toastify';

const REFRESH_INTERVAL = 30000; // 30 s

const STATUS_PRIORITY = { ready: 1, preparing: 2, accepted: 3, pending: 4 };

function elapsedMinutes(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr)) / 60000);
}

function ElapsedBadge({ placedAt, isLate }) {
  const [mins, setMins] = useState(() => elapsedMinutes(placedAt));
  useEffect(() => {
    const id = setInterval(() => setMins(elapsedMinutes(placedAt)), 10000);
    return () => clearInterval(id);
  }, [placedAt]);
  const color = isLate ? '#EF4444' : mins > 25 ? '#F59E0B' : '#10B981';
  return (
    <span style={{ fontWeight: 700, color, fontFamily: 'monospace', fontSize: 14 }}>
      {mins}m {isLate ? '⚠' : ''}
    </span>
  );
}

function CountdownBadge({ estimatedReadyAt }) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const update = () => {
      if (!estimatedReadyAt) { setLabel('—'); return; }
      const diff = Math.floor((new Date(estimatedReadyAt) - Date.now()) / 60000);
      if (diff <= 0) setLabel('Ready!');
      else setLabel(`~${diff}m`);
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, [estimatedReadyAt]);
  return <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{label}</span>;
}

function QueueRow({ order, onMarkPickedUp, onStatusChange, updating }) {
  const elapsed = elapsedMinutes(order.placedAt);
  const rowBg =
    order.status === 'ready'     ? 'rgba(16,185,129,0.07)' :
    order.isLate                  ? 'rgba(239,68,68,0.07)' :
    order.status === 'preparing'  ? 'rgba(139,92,246,0.07)' :
    'transparent';

  const statusColor = ORDER_STATUS_COLORS[order.status] || '#94A3B8';

  return (
    <tr style={{ background: rowBg, transition: 'background 0.3s' }}>
      <td style={{ padding: '12px 16px', fontWeight: 700, fontSize: 13 }}>
        <span style={{ color: '#E85D04', fontFamily: 'monospace' }}>#{order.pickupToken}</span>
      </td>
      <td style={{ padding: '12px 16px', fontSize: 13 }}>
        <div style={{ fontWeight: 600 }}>{order.customerName}</div>
        <div style={{ fontSize: 11, color: '#94A3B8' }}>{order.customerPhone}</div>
      </td>
      <td style={{ padding: '12px 16px', fontSize: 13 }}>{order.restaurantName}</td>
      <td style={{ padding: '12px 16px', fontSize: 12, maxWidth: 200 }}>
        {(order.orderItems || []).slice(0, 2).map((item, i) => (
          <div key={i}>{item.qty}× {item.name}</div>
        ))}
        {(order.orderItems || []).length > 2 && (
          <div style={{ color: '#94A3B8' }}>+{order.orderItems.length - 2} more</div>
        )}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{
          display: 'inline-block', padding: '3px 10px', borderRadius: 20,
          fontSize: 12, fontWeight: 700,
          background: statusColor + '22', color: statusColor,
        }}>
          {ORDER_STATUS_LABELS[order.status] || order.status}
        </span>
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: '#94A3B8' }}>target</div>
        <div style={{ fontWeight: 700 }}>{order.prepTimeMinutes}m</div>
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        <ElapsedBadge placedAt={order.placedAt} isLate={order.isLate} />
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        <CountdownBadge estimatedReadyAt={order.estimatedReadyAt} />
      </td>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {order.status === 'pending' && (
            <Button size="sm" variant="primary" disabled={updating === order.id}
              onClick={() => onStatusChange(order.id, 'accepted')}>Accept</Button>
          )}
          {order.status === 'accepted' && (
            <Button size="sm" variant="primary" disabled={updating === order.id}
              onClick={() => onStatusChange(order.id, 'preparing')}>Start Prep</Button>
          )}
          {order.status === 'preparing' && (
            <Button size="sm" variant="success" disabled={updating === order.id}
              onClick={() => onStatusChange(order.id, 'ready')}>Mark Ready</Button>
          )}
          {order.status === 'ready' && (
            <Button size="sm" variant="success" icon={<CheckCircle size={12} />}
              disabled={updating === order.id}
              onClick={() => onMarkPickedUp(order.id)}>Picked Up</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

const PickupQueue = () => {
  const [queue, setQueue]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [updatingId, setUpdating] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const data = await adminApi.getPickupQueue(null, getPickupQueue);
    const items = Array.isArray(data) ? data : (data?.items || []);
    // Sort by priority: ready first, then late, then by elapsed time
    items.sort((a, b) => {
      const pa = STATUS_PRIORITY[a.status] ?? 9;
      const pb = STATUS_PRIORITY[b.status] ?? 9;
      if (pa !== pb) return pa - pb;
      return new Date(a.placedAt) - new Date(b.placedAt);
    });
    setQueue(items);
    setLastRefresh(new Date());
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), REFRESH_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await adminApi.updateOrderStatus(orderId, newStatus);
      toast.success(`Order → ${ORDER_STATUS_LABELS[newStatus]}`);
    } catch { /* offline */ }
    setQueue(prev => prev.map(o =>
      o.id === orderId ? { ...o, status: newStatus } : o
    ).filter(o => o.status !== 'completed'));
    setUpdating(null);
  };

  const handleMarkPickedUp = async (orderId) => {
    setUpdating(orderId);
    try {
      await adminApi.markPickedUp(orderId);
      toast.success('Order marked as Picked Up!');
    } catch { /* offline */ }
    setQueue(prev => prev.filter(o => o.id !== orderId));
    setUpdating(null);
  };

  const ready    = queue.filter(o => o.status === 'ready');
  const late     = queue.filter(o => o.isLate && o.status !== 'ready');
  const active   = queue.filter(o => !['completed', 'cancelled', 'rejected'].includes(o.status));
  const pending  = queue.filter(o => o.status === 'pending');

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Pickup Queue</h1>
          <p className="admin-page-subtitle">
            Live order queue · Auto-refreshes every 30s
            {lastRefresh && <span style={{ color: '#64748B', marginLeft: 8 }}>
              Last: {lastRefresh.toLocaleTimeString()}
            </span>}
          </p>
        </div>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={() => load()}>
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Active Orders"      value={active.length}   icon={<Clock size={18} />}        color="#3B82F6" />
        <StatCard label="Awaiting Accept"    value={pending.length}  icon={<AlertCircle size={18} />}  color="#F59E0B" />
        <StatCard label="Ready for Pickup"   value={ready.length}    icon={<CheckCircle size={18} />}  color="#10B981" />
        <StatCard label="Late / Overdue"     value={late.length}     icon={<AlertCircle size={18} />}  color="#EF4444" />
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: '#94A3B8' }}>
        {[
          ['rgba(16,185,129,0.15)', 'Ready for pickup'],
          ['rgba(239,68,68,0.1)',   'Late / overdue'],
          ['rgba(139,92,246,0.1)', 'Currently preparing'],
        ].map(([bg, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 14, height: 14, background: bg, borderRadius: 3 }} />
            {label}
          </div>
        ))}
      </div>

      {/* Queue table */}
      <div className="admin-table-wrapper" style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Loader size={28} style={{ animation: 'spin 1s linear infinite', color: '#E85D04' }} />
          </div>
        ) : queue.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748B' }}>
            <CheckCircle size={40} style={{ marginBottom: 8, color: '#10B981' }} />
            <div style={{ fontWeight: 600, fontSize: 16 }}>Queue is empty!</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>All orders have been handled.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--admin-border)', background: 'var(--admin-bg)' }}>
                {['Token', 'Customer', 'Restaurant', 'Items', 'Status', 'Prep Target', 'Elapsed', 'Est. Ready', 'Action']
                  .map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12,
                      fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {queue.map(order => (
                <QueueRow
                  key={order.id}
                  order={order}
                  onMarkPickedUp={handleMarkPickedUp}
                  onStatusChange={handleStatusChange}
                  updating={updatingId}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PickupQueue;
